'use client'
import { ChainUtils } from '@morpho-org/blue-sdk'
import { Avatar, ClipboardButton, DataList } from '@morpho-org/ui-v6/client'
import { formatters, getOraclePriceLabel, UNKNOWN_SYMBOL } from '@morpho-org/ui-v6/utils'
import { TruncatedTooltip } from '@repo/ui'
import { ArrowDiagonalUp } from '@repo/ui/icons'

import type { CollateralRewardEntry } from '@/components/common/CollateralYieldTags'

import { ButtonLink } from '@/components/common/ButtonLink'
import { CollateralYieldTags } from '@/components/common/CollateralYieldTags'
import { WarningIcon } from '@/components/common/WarningIcon'
import { formatTimestampToDate } from '@/helpers/date'
import { getOracleProcedureWarnings } from '@/helpers/warnings'

import type { MarketInfo } from './info.contract'

import InfoMarketDataListLayout from './InfoMarketDataListLayout'

const percentageFormatter = formatters.percent.digits(2).default('-').unit('%')

export interface InfoMarketDataListClientProps {
  chainId: number
  market: MarketInfo
  /** The collateral's native yield APY as a fraction; null when it has none. */
  collateralYield: number | null
  /** Asset-level collateral reward APRs from the market apy breakdown. */
  collateralRewards: CollateralRewardEntry[]
}

/**
 * Info market data list component to display the main market information.
 *
 */
export default function InfoMarketDataListClient({
  chainId,
  market,
  collateralYield,
  collateralRewards
}: InfoMarketDataListClientProps) {
  const { loanToken, collateralToken } = market
  const formattedDate =
    market.creationTimestamp === null ? '-' : formatTimestampToDate(market.creationTimestamp)
  const formattedLltv = formatters.percent
    .digits(2)
    .default('-')
    .trailingZero(false)
    .unit('%')
    .of(BigInt(market.lltv), 18)
  const currentUtilizationFormatted = percentageFormatter.of(market.utilization)
  const formattedTotalMarketSize = formatters.short
    .digits(2)
    .unit('$')
    .default('-')
    .trailingZero(false)
    .of(market.sizeUsd)
  const oracleWarnings = getOracleProcedureWarnings(market.warnings)
  const oraclePrice = getOraclePriceLabel({
    collateralAsset: collateralToken,
    loanAsset: loanToken,
    price: market.oraclePrice
  })

  return (
    <InfoMarketDataListLayout>
      <DataList.Root fullWidth>
        <DataList.Column>
          <DataList.Line>
            <DataList.LeftItem>
              <span className="text-secondary-foreground text-xs">Loan</span>
            </DataList.LeftItem>
            <DataList.RightItem className="w-0 flex-1 justify-end">
              <div className="gap-2xs flex min-w-0 items-center">
                <Avatar src={loanToken.logoUri} size="small" />
                <TruncatedTooltip text={loanToken.symbol ?? UNKNOWN_SYMBOL}>
                  {symbolRef => (
                    <span ref={symbolRef} className="text-foreground min-w-0 truncate text-xs">
                      {loanToken.symbol ?? UNKNOWN_SYMBOL}
                    </span>
                  )}
                </TruncatedTooltip>
                <ClipboardButton
                  size="sm"
                  toCopy={loanToken.address}
                  toastMessage="Loan token address copied to clipboard."
                  tooltipMessage="Copy loan token address."
                />
                <ButtonLink
                  href={ChainUtils.getExplorerAddressUrl(chainId, loanToken.address)}
                  buttonProps={{
                    variant: 'ghost',
                    size: 'sm',
                    iconRight: undefined,
                    iconAlone: <ArrowDiagonalUp />,
                    iconAloneProps: {
                      className: 'text-secondary-foreground'
                    }
                  }}
                />
              </div>
            </DataList.RightItem>
          </DataList.Line>
          <DataList.Line>
            <DataList.LeftItem>
              <span className="text-secondary-foreground text-xs">Collateral</span>
            </DataList.LeftItem>
            <DataList.RightItem className="w-0 flex-1 justify-end">
              <div className="gap-2xs flex min-w-0 items-center">
                <Avatar src={collateralToken?.logoUri} size="small" />
                <TruncatedTooltip
                  text={collateralToken ? (collateralToken.symbol ?? UNKNOWN_SYMBOL) : ''}
                >
                  {symbolRef => (
                    <span ref={symbolRef} className="text-foreground min-w-0 truncate text-xs">
                      {collateralToken && (collateralToken.symbol ?? UNKNOWN_SYMBOL)}
                    </span>
                  )}
                </TruncatedTooltip>
                <CollateralYieldTags
                  nativeYield={collateralYield}
                  collateralRewards={collateralRewards}
                />
                {collateralToken && (
                  <>
                    <ClipboardButton
                      size="sm"
                      toCopy={collateralToken.address}
                      toastMessage="Collateral token address copied to clipboard."
                      tooltipMessage="Copy collateral token address."
                    />
                    <ButtonLink
                      href={ChainUtils.getExplorerAddressUrl(chainId, collateralToken.address)}
                      buttonProps={{
                        variant: 'ghost',
                        size: 'sm',
                        iconRight: undefined,
                        iconAlone: <ArrowDiagonalUp />,
                        iconAloneProps: {
                          className: 'text-secondary-foreground'
                        }
                      }}
                    />
                  </>
                )}
              </div>
            </DataList.RightItem>
          </DataList.Line>
          <DataList.Line>
            <DataList.LeftItem>
              <span
                className="text-secondary-foreground text-xs"
                style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}
              >
                Liquidation LTV
              </span>
            </DataList.LeftItem>
            <DataList.RightItem label={formattedLltv} />
          </DataList.Line>
        </DataList.Column>

        <DataList.Column>
          <DataList.Line>
            <DataList.LeftItem>
              <span className="text-secondary-foreground text-xs">Oracle price</span>
              {oracleWarnings.length > 0 && <WarningIcon warnings={oracleWarnings} isSimple />}
            </DataList.LeftItem>
            <DataList.RightItem label={oraclePrice} />
          </DataList.Line>

          <DataList.Line>
            <DataList.LeftItem>
              <span className="text-secondary-foreground text-xs">Created on</span>
            </DataList.LeftItem>
            <DataList.RightItem label={formattedDate} />
          </DataList.Line>

          <DataList.Line>
            <DataList.LeftItem
              label="Utilization"
              tooltip="Share of supplied liquidity that’s currently borrowed."
            />
            <DataList.RightItem label={currentUtilizationFormatted} />
          </DataList.Line>

          <DataList.Line>
            <DataList.LeftItem
              label="Total market size"
              tooltip="The sum of all borrowed funds, market liquidity, and reallocatable liquidity."
            />
            <DataList.RightItem label={formattedTotalMarketSize} />
          </DataList.Line>
        </DataList.Column>
      </DataList.Root>
    </InfoMarketDataListLayout>
  )
}
